import { useState } from 'react';
import Button from '../../../reusable/Button/Button';
import CopyButton from '../../../reusable/CopyButton/CopyButton';
import DataTable from '../../../reusable/DataTable/DataTable';
import StatusChip from '../../../reusable/StatusChip/StatusChip';
import { toast } from '../../../reusable/Toast/toastStore';
import { useGetContractsQuery, useGetSigningUrlMutation } from '../../../store/api/contractsApi';
import { useGetDashboardStatsQuery } from '../../../store/api/dashboardApi';
import { t } from '../../../i18n/i18n';
import './Contracts.scss';

/** A signed agreement opens on DigiSigner's own page, beside the dashboard. */
const openDocument = (row) => window.open(row.document_link, '_blank', 'noopener,noreferrer');

/** The signing link out of `/dg/get_siging_url/<id>/`, whichever key it rides under. */
const signingUrlOf = (data) =>
  typeof data === 'string' ? data : data?.url ?? data?.signing_url ?? data?.sign_url ?? data?.document_link;

export default function Contracts() {
  const { data, isLoading, isError } = useGetContractsQuery(undefined, {
    // Back from DigiSigner, the list must say what changed there.
    refetchOnMountOrArgChange: true,
  });
  const [getSigningUrl] = useGetSigningUrlMutation();
  const [signingId, setSigningId] = useState(null);

  /**
   * Sign contract: straight to the row's own link once it has one (a signing
   * started and closed unsigned); otherwise ask for one first. DigiSigner is
   * entered in this tab, as the old dashboard did.
   */
  const sign = async (row) => {
    if (row.document_link) {
      window.location.href = row.document_link;
      return;
    }
    setSigningId(row.id);
    try {
      const url = signingUrlOf(await getSigningUrl(row.id).unwrap());
      if (!url) throw new Error('no signing url');
      window.location.href = url;
    } catch {
      setSigningId(null);
      toast.error(t('Could not open the contract'), t('Something went wrong. Please try again.'));
    }
  };
  const contracts = data?.data ?? [];
  const awaiting = contracts.filter((row) => !row.is_verified).length;

  /**
   * An empty list says why for this trader (N57): "No funded accounts yet"
   * was being told to traders with dozens. The Dashboard's own count says
   * which it is; until it's known, the message claims neither.
   */
  const { data: summary, isSuccess: hasSummary } = useGetDashboardStatsQuery();
  const fundedCount = Number(summary?.funded_balance?.account_count) || 0;
  const emptyMessage = isError
    ? t('Could not load your contracts. Please try again in a moment.')
    : !hasSummary
      ? t('No contracts yet.')
      : fundedCount > 0
        ? fundedCount === 1
          ? t("Your {{count}} qualified account has no contract issued yet — each appears here once it's sent for your signature.", { count: fundedCount })
          : t("Your {{count}} qualified accounts have no contract issued yet — each appears here once it's sent for your signature.", { count: fundedCount })
        : t('No qualified accounts yet — contracts appear here once you pass a challenge.');

  const columns = [
    {
      key: 'challenge_name',
      header: 'Challenge Name',
      width: 2.2,
      sortable: true,
    },
    {
      key: 'login_id',
      header: 'Login ID',
      width: 1.2,
      sortable: true,
      render: (row) => (
        <span className="contracts-login">
          {row.login_id}
          <CopyButton value={row.login_id} label={t('Login ID')} />
        </span>
      ),
    },
    {
      key: 'status',
      header: 'Status',
      width: 1.2,
      sortable: true,
      render: (row) => <StatusChip>{t(row.status)}</StatusChip>,
    },
    {
      key: 'actions',
      header: 'Actions',
      width: 1.2,
      align: 'right',
      searchable: false,
      render: (row) => {
        if (row.is_verified) {
          return row.document_link ? (
            <Button variant="toolbar" onClick={() => openDocument(row)}>
              {t('View contract')}
            </Button>
          ) : null;
        }
        // Issued and not signed yet — with a link or without one.
        const busy = signingId === row.id;
        return (
          <Button
            variant="pill"
            height={32}
            paddingInline={14}
            onClick={() => sign(row)}
            disabled={busy}
          >
            {busy ? t('Opening…') : t('Sign contract')}
          </Button>
        );
      },
    },
  ];

  return (
    <div className="contracts">
      <header className="pv-head">
        <div>
          <h1>{t('Contracts list')}</h1>
          <p>
            {t('One analyst agreement per qualified account. Sign each one to unlock performance fee requests on that login.')}
          </p>
        </div>
      </header>

      {awaiting > 0 && (
        <div className="contracts-banner" role="status">
          <span className="contracts-banner__count">{awaiting}</span>
          <p>
            {awaiting === 1
              ? t('One contract is waiting for your signature. Performance fees on that account unlock once signed.')
              : t('{{count}} contracts are waiting for your signature. Performance fees on those accounts unlock once signed.', { count: awaiting })}
          </p>
        </div>
      )}

      <DataTable
        className="contracts-table"
        title={t('Contracts list')}
        icon="table-accounts"
        columns={columns}
        rows={contracts}
        loading={isLoading}
        minWidth={620}
        searchPlaceholder={t('Search challenge or login')}
        emptyMessage={emptyMessage}
      />
    </div>
  );
}
